/**
 * Which setup questions a particular person is asked, and whether to ask at all.
 *
 * WHY THIS IS NOT ONE LIST
 *
 * Six of the eight questions are about the PROJECT — what it is called, where
 * its issues come from, what "done" means. They are answered once, by somebody
 * who is allowed to change settings, and everyone else lives with the answers.
 *
 * Two are about the PERSON — what to call you, and when you want to be told
 * about comments. Those belong to whoever is sitting there, and somebody joining
 * a board set up months ago still needs to answer them.
 *
 * Running one list for both is wrong in both directions: it either walks a new
 * reviewer through settings that are not theirs to change, or it never asks them
 * their name and the board shows their email address on every comment they
 * write. Both of those actually happened before this existed.
 *
 * So there are two markers, and this decides what is left for you.
 */
import type { ProjectMeta } from './types';
import type { Profile } from './people';

export type StepId =
  | 'name'
  | 'source'
  | 'repo'
  | 'done'
  | 'you'
  | 'team'
  | 'assistants'
  | 'notify';

export type StepScope = 'project' | 'person';

export interface Step {
  id: StepId;
  scope: StepScope;
  /** The question, as a person would ask it. */
  title: string;
}

/**
 * Every question, in the order they are asked.
 *
 * The personal one sits in the middle rather than at the end on purpose: it
 * comes after the project's own details and before the people questions, which
 * is where "and what should we call you?" belongs in the conversation.
 */
export const STEPS: Step[] = [
  { id: 'name', scope: 'project', title: 'What is this project called?' },
  { id: 'source', scope: 'project', title: 'Where do the issues come from?' },
  { id: 'repo', scope: 'project', title: 'Where does the code live?' },
  { id: 'done', scope: 'project', title: 'What does “done” mean here?' },
  { id: 'you', scope: 'person', title: 'What should we call you?' },
  { id: 'team', scope: 'project', title: 'Who else should have access?' },
  { id: 'assistants', scope: 'project', title: 'Is an assistant working on this?' },
  { id: 'notify', scope: 'person', title: 'When do you want to be told?' },
];

export interface Who {
  /** Whether this person may change the project's settings. */
  canSettings: boolean;
  meta: Pick<ProjectMeta, 'setUpAt' | 'name'>;
  /**
   * How many people have access to this board, the owner included. Used to
   * decide whether "who else should have access?" has effectively been answered
   * already — a board someone was invited to, or that the New Project wizard
   * created with a team, should not be asked it again on first open.
   */
  memberCount: number;
  /**
   * `firstName` and `lastName` are here to answer a question rather than to be
   * displayed: whether somebody has ALREADY been named, by the admin who added
   * them. See `alreadyNamed` below.
   */
  profile: Pick<Profile, 'setUpAt' | 'firstName' | 'lastName'> | undefined;
}

/**
 * WHETHER SOMEBODY ARRIVED WITH A NAME ALREADY ON THEM.
 *
 * `firstName`/`lastName` rather than `name`, and the difference is the whole
 * point. Every person added gets a `name` — it falls back to the part of their
 * address before the @, so that nobody is shown to the workspace as a raw email
 * while they wait to sign in. Reading THAT as "already named" would skip the
 * question for everybody, including the people who most need asking.
 *
 * The halves are only written when an admin actually typed them on the Add
 * someone page, so they are the honest signal for "this was answered by
 * somebody else, on their behalf".
 */
function alreadyNamed(profile: Who['profile']): boolean {
  return Boolean(profile?.firstName?.trim() || profile?.lastName?.trim());
}

/**
 * What is still to answer.
 *
 * Empty means nothing to ask — which is the answer for most people most of the
 * time, and the reason nothing appears on their board.
 */
export function stepsFor({ canSettings, meta, memberCount, profile }: Who): Step[] {
  const projectOutstanding = canSettings && !meta.setUpAt;
  const personOutstanding = !profile?.setUpAt;
  return STEPS.filter((s) => {
    if (s.scope === 'project') {
      if (!projectOutstanding) return false;
      /*
       * DO NOT RE-ASK WHAT IS ALREADY ANSWERED (Vinnie, 14 September).
       *
       * The project-level marker (`setUpAt`) is all-or-nothing, so a board that
       * has never been "set up" was offered every project question — even the
       * ones the New Project wizard, or being invited to an existing board, had
       * already answered. Right after the wizard the board would announce it was
       * "not set up" and re-ask the name it was just given. So two of the
       * project questions now gate on their own answer:
       *   · a project always has a name by the time it exists, so `name` never
       *     needs asking here (it stays in the from-Settings run via allStepsFor);
       *   · a board with anyone beyond its owner has effectively answered "who
       *     else should have access", so `team` is not re-asked.
       * The rest (issue source, definition of done, repo, assistants) the wizard
       * does not capture, so they remain genuinely outstanding and still appear.
       */
      if (s.id === 'name' && meta.name?.trim()) return false;
      if (s.id === 'team' && memberCount > 1) return false;
      return true;
    }
    if (!personOutstanding) return false;
    /*
     * DO NOT ASK WHAT SOMEBODY ELSE HAS ALREADY ANSWERED.
     *
     * Vinnie, 15 August: "why do I have to go through the 'who are you' screen
     * if all my information has already been entered by the admin — that was
     * the reason for adding the user screen."
     *
     * Quite. The Add someone page exists so that a person arrives with a name
     * and a job title on them, and asking for both again on their first visit
     * makes the whole page pointless — worse than pointless, because it teaches
     * the admin that filling it in achieves nothing.
     *
     * ONLY THIS STEP. `notify` is the other personal question and it stays:
     * when somebody wants to be interrupted is genuinely theirs, and no admin
     * can answer it on their behalf. Skipping a question nobody can answer for
     * them would just mean it never gets asked.
     */
    if (s.id === 'you' && alreadyNamed(profile)) return false;
    return true;
  });
}

/**
 * Every question this person may answer, whatever has been answered before.
 *
 * For running the questions again deliberately, from Settings. `stepsFor` is
 * driven by the markers, so once a project is set up it returns nothing — and a
 * button that opened an empty wizard would look broken rather than finished.
 *
 * Permission still applies: somebody who cannot change settings gets the two
 * questions that are about them and no more, however they got here.
 */
export function allStepsFor(canSettings: boolean): Step[] {
  return STEPS.filter((s) => s.scope === 'person' || canSettings);
}

/** Whether to offer setup at all. */
export function needsSetup(who: Who): boolean {
  return stepsFor(who).length > 0;
}

/**
 * Which markers to stamp when the questions are closed — by finishing OR by
 * skipping.
 *
 * Only the scopes that were actually asked. Somebody who was shown two personal
 * questions must not silently mark the whole project as set up: the next person
 * who could have configured it would never be offered the chance.
 */
export function scopesAnswered(steps: Step[]): { project: boolean; person: boolean } {
  return {
    project: steps.some((s) => s.scope === 'project'),
    person: steps.some((s) => s.scope === 'person'),
  };
}
